/* eslint-disable @typescript-eslint/member-ordering */
import {
	ChangeDetectorRef,
	Component,
	ElementRef,
	HostListener,
	Input,
	OnChanges,
	OnDestroy,
	QueryList,
	ViewChild,
	ViewChildren
} from '@angular/core';
import { IReportItem } from '@interfaces/interfaces';
import {
	IColorRange,
	ListReportItem,
	ListReportColorGauge,
	ListReportColorRangeEntry,
	ListReportEntry,
	ListReportRange
} from '@interfaces/reports';
import { SessionItemData } from '../../../services/session-item-data';
import { Subscription } from 'rxjs';
import { select, Store } from '@ngrx/store';
import * as Reducers from '../../../../store/reducers';
import { InterpolationService } from '@app/services/interpolation/interpolation.service';
import { isNil } from 'lodash';
import { ListReportEntryWithVisibility } from '@workflow/components/report-card/report-filter.service';
import { HIDE_MISSINGVALUE } from '@app/constants';

interface DisplayListReportEntry {
	label: string;
	type: 'Text' | 'ColorRange';
	value: string | number;
	unit?: string;
	color: string;
	range: number;
	fehlwert?: boolean;
	template: string;
	tooltip: string | null;
	visible: boolean;
	printable: boolean;
}

@Component({
	selector: 'workflow-list-report',
	templateUrl: './list-report.component.html',
	styleUrls: ['./list-report.component.scss']
})
export class ListReportComponent implements OnChanges, OnDestroy {

	@Input() report: IReportItem;
	@Input() data: SessionItemData;
	listReport: ListReportItem;
	displayReportEntries: DisplayListReportEntry[] = undefined;
	title= '';
	hasFehlwert: boolean;
	language: string;
	tooltip: string | null;
	private subscription = new Subscription();

	private _labelHeader: ElementRef<HTMLCanvasElement>;
	@ViewChild('labelHeader')
	private set labelHeader(_labelHeader: ElementRef<HTMLCanvasElement>) {
		this._labelHeader = _labelHeader;
		this.onWindowResize();
	}
	private get labelHeader(): ElementRef<HTMLCanvasElement> {
		return this._labelHeader;
	}

	private _labels: QueryList<ElementRef<HTMLCanvasElement>>;
	@ViewChildren('label')
	private set labels(_labels: QueryList<ElementRef<HTMLCanvasElement>>) {
		this._labels = _labels;
		this.onWindowResize();
	}
	private get labels(): QueryList<ElementRef<HTMLCanvasElement>> {
		return this._labels;
	}

	@HostListener('window:resize')
	onWindowResize(): void {
		if (this.labelHeader?.nativeElement && this.labels?.length > 0) {
			const hasEllipsis = this.labelHeader.nativeElement.clientWidth < this.labelHeader.nativeElement.scrollWidth;
			this.tooltip = hasEllipsis ? this.listReport.title : null;
			this.labels.forEach((label, index) => {
				const displayReportHasEllipsis = label.nativeElement.clientWidth < label.nativeElement.scrollWidth;
				return this.displayReportEntries[index].tooltip = displayReportHasEllipsis ? this.displayReportEntries[index].label : null;
			});
			this.changeDetectorRef.detectChanges();
		}
	}

	constructor(private store: Store<never>, private interpolationService: InterpolationService, private readonly changeDetectorRef: ChangeDetectorRef) {
		this.subscription.add(this.store.pipe(select(Reducers.getSettings)).subscribe(state => {
			this.language = state.locale;
		}));
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

	ngOnChanges(): void {
		if (this.data && this.report) {
			this.displayReportEntries = undefined;

			this.listReport = this.report as ListReportItem;
			this.title = this.listReport.title;

			const converter = new ListReportConverter(this.reportData, this.listReport, this.language, this.interpolationService);
			this.displayReportEntries = converter.convert().filter(data => !isNil(data.value) && data.value !== HIDE_MISSINGVALUE);
			this.hasFehlwert = this.displayReportEntries.some(r => r.fehlwert);
		}
	}

	/**
	 * Wenn der listReport ein keyPrefix gesetzt hat, dann übergeben wir dem Report nur die Daten unterhalb des keys.
	 * Alle anderen Auswertung im ListReportConverter ignorieren dann die keyPrefix und arbeiten mit den normalen keys in
	 * dem Teil-Objekt
	 */
	private get reportData(): SessionItemData {
		if (this.listReport?.keyPrefix) {
			const prefixData = this.data?.getProperty(this.listReport.keyPrefix);
			return new SessionItemData(prefixData);
		}
		return this.data;
	}
}

export class ListReportConverter {

	static readonly defaultColorGauge: ListReportColorGauge = {
		colorRanges: [
			{color: 'AAAAAA'},
		]
	};
	private separator: string;

	constructor(private data: SessionItemData, private listReport: ListReportItem, private language: string, private interpolationService: InterpolationService) {
		this.separator = this.listReport.flatKeys ? '_' : '.';
	}

	convert(): DisplayListReportEntry[] {
		const displayReports = [] as DisplayListReportEntry[];
		for (const entry of (this.listReport.entries) as ListReportEntryWithVisibility[]) {

			const displayReportEntry: DisplayListReportEntry = {
				label: entry.label,
				type: null,
				value: null,
				unit: null,
				color: null,
				range: null,
				fehlwert: false,
				template: null,
				tooltip: null,
				visible: false,
				printable: false,
			};

			displayReportEntry.type = entry.type;
			displayReportEntry.visible = entry.visible;
			displayReportEntry.printable = entry.printable;
			const {realValue, missingValue} = this.readValue(entry);

			if (isNil(realValue) && isNil(missingValue)) {
				continue;
			} else if (!isNil(missingValue)) {
				displayReportEntry.fehlwert = true;
			}
			const value = missingValue ?? realValue;

			if (entry.displayValueMap) {
				displayReportEntry.value = entry.displayValueMap[value];
			} else {
				displayReportEntry.value = value;
			}

			let defaultTemplate: string;
			if (typeof(displayReportEntry.value) === 'number') {
				defaultTemplate = '{{ value | number: numberFormat : language }}';
			} else {
				defaultTemplate = '{{ value }}';
			}

			if (entry.type === 'ColorRange') {
				const colorEntry = entry as ListReportColorRangeEntry;
				displayReportEntry.unit = colorEntry.unit || '';

				const range = this.readRange(colorEntry);
				if (range) {
					const rangeValue = this.readRangeValue(colorEntry) as number;
					const rangeDelta = range.max - range.min;
					displayReportEntry.range = (( rangeValue - range.min ) * 100 ) / rangeDelta;
					if (displayReportEntry.range < 0) {
						displayReportEntry.range = 0;
					} else if (displayReportEntry.range > 100) {
						displayReportEntry.range = 100;
					}
					if (range.reversed) {
						displayReportEntry.range = 100 - displayReportEntry.range;
					}
				} else {
					displayReportEntry.range = 0;
				}

				const colorGauge = this.readColor(colorEntry);
				const colorValue = this.readColorValue(colorEntry);

				displayReportEntry.color = '#' + ListReportConverter.defaultColorGauge.colorRanges[0].color;
				for (const colorRange of colorGauge.colorRanges) {
					if (colorRange.min < colorRange.max && colorValue >= colorRange.min && colorValue < colorRange.max) {
						displayReportEntry.color = '#' + colorRange.color;
						break;
					} else if (colorRange.min > colorRange.max && colorValue <= colorRange.min && colorValue > colorRange.max) {
						displayReportEntry.color = '#' + colorRange.color;
						break;
					}
				}

				const context = {
					value: displayReportEntry.value,
					unit: displayReportEntry.unit,
					color: displayReportEntry.color,
					type: displayReportEntry.type,
					range: displayReportEntry.range,
					fehlwert: displayReportEntry.fehlwert,
					label: displayReportEntry.label,
					language: this.language,
					numberFormat: `1.0-3`,
				};

				displayReportEntry.template = this.interpolationService.interpolate(colorEntry.template || defaultTemplate, context);
			} else if (entry.type === 'Text') {
				const context = {
					value: displayReportEntry.value,
					type: displayReportEntry.type,
					label: displayReportEntry.label,
					language: this.language,
					numberFormat: `1.0-3`,
				};
				displayReportEntry.template = this.interpolationService.interpolate(defaultTemplate, context);
			}
			displayReports.push(displayReportEntry);
		}
		return displayReports;
	}

	private readValue(entry: ListReportEntry): { realValue: string | number | null, missingValue: string | number | null } {
		const data = {realValue: null, missingValue: null};
		const valueKey = entry.valueKey;

		if (!valueKey) {
			data.realValue = this.data.getProperty(entry.key);
			data.missingValue = this.data.getProperty(`${entry.key}_missingValue`);
		}
		else {
			data.realValue = this.data.getProperty(`${entry.key}${this.separator}${valueKey}`);
			data.missingValue = this.data.getProperty(`${entry.key}${this.separator}${valueKey}_missingValue`);
		}

		return data;
	}

	private readRange(entry: ListReportColorRangeEntry): ListReportRange {
		const range = entry.range || this.listReport.range;

		if (!range) {
			return undefined;
		}

		const maxKey = range.maxKey;
		const minKey = range.minKey;

		const min = minKey && this.data.getProperty(`${entry.key}${this.separator}${minKey}`) || range.min;
		const max = maxKey && this.data.getProperty(`${entry.key}${this.separator}${maxKey}`) || range.max;

		return {
			min: min ?? Number.MIN_SAFE_INTEGER,
			max: max ?? Number.MAX_SAFE_INTEGER,
			reversed: range.reversed,
		};
	}

	private readRangeValue(entry: ListReportColorRangeEntry): number {
		const range = entry.range || this.listReport.range;
		const valueKey = range?.valueKey || entry.valueKey;

		if (!valueKey) {
			const realValue = this.data.getProperty(entry.key);
			const missingValue = this.data.getProperty(`${entry.key}_missingValue`);
			return missingValue ?? realValue;
		}

		const realValue = this.data.getProperty(`${entry.key}${this.separator}${valueKey}`);
		const missingValue = this.data.getProperty(`${entry.key}${this.separator}${valueKey}_missingValue`);
		return missingValue ?? realValue;
	}

	private readColorValue(entry: ListReportColorRangeEntry): number {
		const colorGauge = entry.colorGauge || this.listReport.colorGauge;
		const valueKey = colorGauge?.valueKey || entry.valueKey;

		if (!valueKey) {
			const realValue = this.data.getProperty(entry.key);
			const missingValue = this.data.getProperty(`${entry.key}_missingValue`);
			return missingValue ?? realValue;
		}

		const realValue = this.data.getProperty(`${entry.key}${this.separator}${valueKey}`);
		const missingValue = this.data.getProperty(`${entry.key}${this.separator}${valueKey}_missingValue`);
		return missingValue ?? realValue;
	}

	private readColor(entry: ListReportColorRangeEntry): ListReportColorGauge {
		const colorGauge = entry.colorGauge || this.listReport.colorGauge;
		if (!colorGauge) {
			return ListReportConverter.defaultColorGauge;
		}

		const processColorRanges: IColorRange[] = [];
		for (const colorRange of colorGauge.colorRanges) {

			const maxKey = colorRange.maxKey;
			const minKey = colorRange.minKey;

			const min = minKey && this.data.getProperty(`${entry.key}${this.separator}${minKey}`) || colorRange.min;
			const max = maxKey && this.data.getProperty(`${entry.key}${this.separator}${maxKey}`) || colorRange.max;

			processColorRanges.push({
				min: min ??  Number.MIN_SAFE_INTEGER,
				max: max ??  Number.MAX_SAFE_INTEGER,
				color: colorRange.color,
			});
		}

		return {
			colorRanges: processColorRanges,
			valueKey: colorGauge.valueKey,
		};
	}
}
